import {faChartSimple, faGaugeSimpleHigh} from '@fortawesome/pro-regular-svg-icons';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {Segmented} from 'antd';
import {useState} from 'react';

import {SemiCircleProgress} from '../../../../component/semi-circle-progress/semi-circle-progress';
import {Text} from '../../../../component/text/text';
import {AdditionalInfo} from '../../../../layout/additional-info/additional-info';
import {PageCard} from '../../../../layout/page-card/page-card';
import {useDomainConfig} from '../../../../provider/domain-config/domain-config-hook';
import {DomainNameEnum} from '../../../../provider/domain-config/domain-config-type';
import {Locale} from '../../../../provider/locale/locale';
import {useLocale} from '../../../../provider/locale/locale-hook';
import {lpsFeatureUserIds} from '../../../../provider/user/user-context-helper';
import {useUser} from '../../../../provider/user/user-hook';
import {useLpsBenchmarks} from '../../../../service/dashboard/dashboard-lps/dashboard-lps-hook';
import {DashboardLpsEnum} from '../dashboard-type';
import {LpsCard} from '../lps-card/lps-card';
import {LpsChart} from '../lps-chart/lps-chart';
import {LpsRatingStatus} from '../lps-rating-status/lps-rating-status';

import * as styles from './lps-page-card.scss';

export function LpsPageCard(): JSX.Element | null {
    const {getLocalizedString} = useLocale();
    const {user} = useUser();
    const {domainName} = useDomainConfig();
    const canShowLps = Boolean(user?.id && lpsFeatureUserIds.has(user.id) && domainName !== DomainNameEnum.brandWizard);

    const [lpsSegment, setLpsSegment] = useState<DashboardLpsEnum>(DashboardLpsEnum.LPS);
    const {data: benchmarksData} = useLpsBenchmarks();

    if (!canShowLps) {
        return null;
    }

    return (
        <PageCard
            title={
                <div className={styles.LpsPageCard_lpsContainer}>
                    <AdditionalInfo leftNode={<Locale stringKey="DASHBOARD_PAGE__LSP_TITLE" />}>
                        <Locale stringKey="DASHBOARD_PAGE__LPS__ADDITIONAL_INFO" />
                    </AdditionalInfo>
                    <Segmented
                        onChange={setLpsSegment}
                        options={[
                            {label: <FontAwesomeIcon icon={faGaugeSimpleHigh} />, value: DashboardLpsEnum.LPS},
                            {label: <FontAwesomeIcon icon={faChartSimple} />, value: DashboardLpsEnum.LpsChart},
                        ]}
                        value={lpsSegment}
                    />
                </div>
            }
        >
            {lpsSegment === DashboardLpsEnum.LPS && (
                <div className={styles.LpsPageCard_lpsSemiCircleWrapper}>
                    <div className={styles.LpsPageCard_lpsSemiCircleContainer}>
                        <SemiCircleProgress />
                        <LpsRatingStatus />
                        <Text className={styles.LpsPageCard_lpsDescription} secondary>
                            <Locale stringKey="DASHBOARD_PAGE__LPS_DESCRIPTION" />
                        </Text>
                    </div>
                    {benchmarksData && (
                        <div className={styles.LpsPageCard_lpsCardContainer}>
                            <LpsCard
                                secondaryText={getLocalizedString(
                                    'DASHBOARD_PAGE__LPS_CARD_INDUSTRY_AVERAGE_DESCRIPTION'
                                )}
                                title={getLocalizedString('DASHBOARD_PAGE__LPS_CARD_INDUSTRY_AVERAGE_TITLE')}
                                value={Math.round(benchmarksData.avgLps || 0)}
                            />
                            <LpsCard
                                secondaryText={getLocalizedString('DASHBOARD_PAGE__LPS_CARD_TOP_VALUE_DESCRIPTION')}
                                title={getLocalizedString('DASHBOARD_PAGE__LPS_CARD_TOP_VALUE_TITLE')}
                                value={Math.round(benchmarksData.maxLps || 0)}
                            />
                        </div>
                    )}
                </div>
            )}
            {lpsSegment === DashboardLpsEnum.LpsChart && <LpsChart />}
        </PageCard>
    );
}
